import { useEffect, useState } from 'react';

import { AlertDialogAction } from '@radix-ui/react-alert-dialog';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  format,
  isAfter,
  isBefore,
  isEqual,
  isValid,
  parse,
  startOfDay,
} from 'date-fns';
import {
  AlertCircleIcon,
  CheckIcon,
  InfoIcon,
  Loader2Icon,
  MinusCircleIcon,
  PlusCircleIcon,
  RefreshCwIcon,
  XIcon,
} from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useSnapshot } from 'valtio';

import { FormUnsavedConfirmationWrapper } from '../../../components/pages/backoffice/form-unsaved-confirmation-wrapper.jsx';
import { BackOfficePermissionsRedirectWrapper } from '../../../components/pages/backoffice/permissions-redirect-wrapper.jsx';
import CustomerCoupons from '../../../components/pages/backoffice/printables/customer-coupons-receipt.jsx';
import CancelButton from '../../../components/buttons/cancel-button.jsx';
import Error from '../../../components/feedback/error.jsx';
import Loading from '../../../components/feedback/loading.jsx';
import Heading from '../../../components/typography/heading.jsx';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog.jsx';
import { Button } from '../../../components/ui/button.jsx';
import { Checkbox } from '../../../components/ui/checkbox.jsx';
import { Combobox } from '../../../components/ui/combobox.jsx';
import { DateInputWithDatePicker } from '../../../components/ui/date-picker.jsx';
import FormCard from '../../../components/ui/form-card.jsx';
import FormFooter from '../../../components/ui/form-footer.jsx';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form.jsx';
import { InputCurrency } from '../../../components/ui/input-currency.jsx';
import { DebouncedInput } from '../../../components/ui/input-debounced.jsx';
import { Input } from '../../../components/ui/input.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select.jsx';
import { msCatalystApi } from '../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../services/state/auth-state.jsx';
import { print } from '../../../helpers/print.js';
import {
  DATE_FORMAT,
  DATETIME_FORMAT_STANDARD,
  DISTRICTS,
} from '../../../utils/constants.js';
import { formatDateToISO } from '../../../utils/format.js';
import { REGEX_DATE_EU } from '../../../utils/regex.js';

export function InvoicesCreate() {
  const navigate = useNavigate();
  const { user } = useSnapshot(authState);
  const [searchParams] = useSearchParams();

  const [submitDialog, setSubmitDialog] = useState(false);

  const formCreateInvoice = useForm({
    mode: 'onChange',
    defaultValues: {
      invoices: [
        {
          code: '',
          amount: 0,
          issuedDate: '',
          shopId: '',
        },
      ],
      campaign: {
        name: '',
        id: '',
        minEntryAmount: '',
        startDate: '',
        closeDate: '',
      },
      customer: {
        cc: '',
        id: '',
        firstName: '',
        lastName: '',
        birthDate: '',
        email: '',
        phone: '',
        address: '',
        district: '',
        zipCode: '',
        consentTermsOfParticipation: null,
      },
    },
    /*
    resolver: yupResolver(
      yup.object().shape({
        campaign: yup
          .object()
          .shape({
            name: yup.string(),
            id: yup.string(),
            minEntryAmount: yup.number(),
          })
          .required('Campanha é um campo obrigatório'),

        customer: yup
          .object()
          .shape({
            cc: yup
              .string()
              .required(
                'Por favor insira um Cliente com Nº de Identificação Válido',
              ),
            email: yup
              .string()
              .required(
                'Por favor insira um Cliente com Nº de Identificação Válido',
              ),
          })
          .default(undefined),

        invoices: yup.array(
          yup.object().shape({
            code: yup.string().required('N.º da fatura é um campo obrigatório'),

            shopId: yup
              .object()
              .shape({
                value: yup.string(),
                label: yup.string(),
              })
              .default(undefined)
              .required('Loja é um campo obrigatório'),

            issuedDate: yup
              .string()
              .required('Data de Emissão da Fatura é um campo obrigatório')
              .matches(
                REGEX_DATE_EU,
                'Data de Emissão tem de seguir o formato: dd-MM-yyyy',
              )
              .test('validDate', 'Insira uma data válida', function (value) {
                try {
                  const parsedDate = parse(value, 'dd-MM-yyyy', new Date());
                  const isValidDate = isValid(parsedDate);

                  return isValidDate;
                } catch (error) {
                  return false;
                }
              })
              .test(
                'dateWithinRange',
                'Data de Emissão deve estar entre a data de início e a data de término da campanha',
                function (value) {
                  const { from } = this;
                  const parent = from[1].value;
                  const campaign = parent.campaign;

                  const parsedIssuedDate = parse(
                    value,
                    DATE_FORMAT,
                    new Date(),
                  );
                  const parsedStartDate = startOfDay(
                    parse(
                      campaign.startDate,
                      DATETIME_FORMAT_STANDARD,
                      new Date(),
                    ),
                  );
                  const parsedCloseDate = startOfDay(
                    parse(
                      campaign.closeDate,
                      DATETIME_FORMAT_STANDARD,
                      new Date(),
                    ),
                  );

                  // Ensure issuedDate is within startDate and closeDate range
                  if (
                    isValid(parsedIssuedDate) &&
                    isValid(parsedStartDate) &&
                    isValid(parsedCloseDate)
                  ) {
                    return (
                      (isAfter(parsedIssuedDate, parsedStartDate) ||
                        isEqual(parsedIssuedDate, parsedStartDate)) &&
                      (isBefore(parsedIssuedDate, parsedCloseDate) ||
                        isEqual(parsedIssuedDate, parsedCloseDate))
                    );
                  }

                  return false;
                },
              ),
          }),
        ),
      }),
    ),
    */
  });

  const {
    fields: invoicesField,
    append: appendInvoices,
    remove: removeInvoices,
  } = useFieldArray({
    control: formCreateInvoice.control,
    name: 'invoices',
  });

  const watchCampaignField = formCreateInvoice.watch('campaign');
  // const watchCustomerFieldId = formCreateInvoice.watch('customer.id');
  const watchCustomerFieldCc = formCreateInvoice.watch('customer.cc');
  const watchCustomerFieldTermsConsent = formCreateInvoice.watch(
    'customer.consentTermsOfParticipation',
  );
  const watchCustomerFirstNameField =
    formCreateInvoice.watch('customer.firstName');
  const watchCustomerLastNameField =
    formCreateInvoice.watch('customer.lastName');
  const watchInvoicesField = formCreateInvoice.watch('invoices');

  const queryCampaign = useQuery({
    queryKey: ['active_campaign_form'],
    queryFn: async () => {
      const { data: ongoingCampaing } =
        await msCatalystApi.get(`campaigns/ongoing`);
      const campaignData = ongoingCampaing.data;

      if (!campaignData) {
        return null;
      }

      formCreateInvoice.setValue('campaign', {
        name: campaignData.campaign.name,
        id: campaignData.campaign.ROWID,
        minEntryAmount: campaignData.campaign.minEntryAmount,
        startDate: campaignData.campaign.startDate,
        closeDate: campaignData.campaign.closeDate,
      });

      return campaignData;
    },
  });

  const queryCustomerFromCc = useQuery({
    queryKey: ['get-customer', watchCustomerFieldCc],
    queryFn: async () => {
      const { data: customer } = await msCatalystApi.get(
        `/customers?cc=${watchCustomerFieldCc}`,
      );

      console.log('customer?', customer);

      if (!customer.data.length) {
        formCreateInvoice.setError(
          'customer.cc',
          'Cliente não existente no sistema',
        );

        return null;
      }

      const customerData = customer.data[0];

      formCreateInvoice.setValue('customer', {
        ...customerData,
        id: customerData?.ROWID,
      });

      return customer.data[0];
    },
    enabled: watchCustomerFieldCc !== '',
  });

  const queryWatchListCheck = useQuery({
    queryKey: [
      'check-watchlist',
      queryCustomerFromCc.data?.cc,
      queryCustomerFromCc.data?.firstName,
      queryCustomerFromCc.data?.lastName,
    ],
    queryFn: async () => {
      const { cc, firstName, lastName } = queryCustomerFromCc.data;
      const { data: watchListRes } = await msCatalystApi.get(
        `/eligibility-watchlist/check?identificationDoc=${cc}&name=${firstName} ${lastName}`,
      );

      return watchListRes;
    },
    enabled: !!queryCustomerFromCc.data,
  });

  useEffect(() => {
    searchParams.forEach((value, key) => {
      formCreateInvoice.setValue(key, value);
    });
  }, [searchParams]);

  useEffect(() => {
    let coupon = localStorage.getItem('coupon_to_create');

    if (!coupon) return;

    coupon = JSON.parse(coupon);

    Object.keys(coupon).forEach((key) => {
      formCreateInvoice.setValue(key, coupon[key], {
        shouldDirty: true,
        shouldValidate: true,
      });
    });

    localStorage.removeItem('coupon_to_create');
  }, []);

  const createClient = () => {
    localStorage.setItem(
      'coupon_to_create',
      JSON.stringify(formCreateInvoice.getValues()),
    );
    navigate(`/backoffice/customers/create?cc=${watchCustomerFieldCc}`);
  };

  const mutationCreateCustomerConsentRequest = useMutation({
    mutationFn: async (data) => {
      try {
        const { data: searchConsentRequestRes } = await msCatalystApi.get(
          `customers/consent-requests/search/${data.cc}`,
        );

        if (searchConsentRequestRes?.data) {
          await msCatalystApi.delete(
            `/customers/consent-requests/${searchConsentRequestRes.data.ROWID}`,
          );
        }

        const newCustomerObj = { ...data.customer };

        delete newCustomerObj.id;
        delete newCustomerObj.ROWID;
        delete newCustomerObj.CREATORID;
        delete newCustomerObj.MODIFIEDTIME;
        delete newCustomerObj.CREATEDTIME;

        const { data: createConsentRequest } = await msCatalystApi.post(
          `/customers/consent-requests`,
          {
            customer: JSON.stringify(newCustomerObj),
            searchCustomerIdentificationDoc: data.customer.cc,
            searchCustomerEmail: data.customer.email,
            staff: `${user.firstName} ${user.lastName}`,
            action: 'update-consent',
            currentCustomerIdentificationDoc: data.customer.cc,
            currentCustomerEmail: data.customer.email,

            customerUserId: data.ids.userId,
            customerId: data.ids.customerId,
          },
        );

        return createConsentRequest.data;
      } catch (error) {
        throw error;
      }
    },
  });

  const onSubmitUpdateConsentTerms = async (data) => {
    await mutationCreateCustomerConsentRequest.mutateAsync({
      customer: {
        ...data,
        role: queryCustomerFromCc.data?.role,
      },
      ids: {
        userId: queryCustomerFromCc.data?.userId,
        customerId: queryCustomerFromCc.data?.ROWID,
      },
    });

    toast.success(
      'Criação de pedido de aprovação de consentimento com sucesso',
    );
  };

  const onFormSubmit = async (data) => {
    try {
      const createdInvoices = [];

      for (const invoice of data.invoices) {
        const formData = new FormData();

        formData.append(
          'data',
          JSON.stringify({
            code: invoice.code,
            amount: invoice.amount,
            status: 'Aprovada',
            issuedDate: formatDateToISO(invoice.issuedDate),
            shopId: invoice.shopId.value,
            campaignId: data.campaign?.id,
            customerId: data.customer?.id,
            proof: 'Receipt',
            // createdBy: user.details.ROWID,
            createdIn: 'Backoffice',
          }),
        );

        const { data: newInvoice } = await msCatalystApi.post(
          `/invoices`,
          formData,
        );

        const createdInvoice = newInvoice.data;

        createdInvoices.push(createdInvoice.ROWID);

        const numEntries = Math.floor(
          invoice.amount / data.campaign?.minEntryAmount,
        );

        await msCatalystApi.get(
          `/invoices/generate-coupons/${createdInvoice.ROWID}`,
        );

        toast.success(`Fatura ${invoice.code} inserida com sucesso!`, {
          description: `Foram gerados ${numEntries} cupões!`,
        });
      }

      const { data: couponsData } = await msCatalystApi.get(
        `/coupons/all?invoices.ROWID=in:${createdInvoices.join(',')}`,
      );

      setTimeout(() => {
        print({
          component: (
            <CustomerCoupons
              user={user}
              customer={queryCustomerFromCc.data}
              coupons={couponsData?.data.map((item) => item.coupons)}
              campaign={{
                name: watchCampaignField.name,
                legalPhrase: queryCampaign.data?.campaign?.legalPhrase,
              }}
            />
          ),
        });
      }, 500);

      navigate(`/backoffice/invoices`);
    } catch (err) {
      const message = err?.response?.data?.message;

      if (
        message &&
        message?.toLowerCase().includes('duplicate value for uniquecode')
      ) {
        toast.error('Fatura duplicada - Esta fatura já existe no sistema');
      } else {
        toast.error(
          'Erro ao inserir fatura, não serão gerados quaisquer cupões',
        );
      }
      return;
    }
  };

  console.log('formCreateInvoice', formCreateInvoice.getValues());
  console.log('queryCustomerFromCc.data', queryCustomerFromCc.data);
  console.log('queryCustomerFromCc.error', queryCustomerFromCc.error);

  return (
    <BackOfficePermissionsRedirectWrapper>
      <Heading className="pb-6">Inserir Fatura(s)</Heading>

      {queryCampaign.isLoading ? (
        <Loading />
      ) : queryCampaign.isError ? (
        <Error />
      ) : !queryCampaign?.data ? (
        <div className="flex h-[70dvh] items-center justify-center">
          <h1 className="text-2xl">
            De momento não existe nenhuma campanha a decorrer!
          </h1>
        </div>
      ) : (
        <FormUnsavedConfirmationWrapper form={formCreateInvoice}>
          <Form {...formCreateInvoice}>
            <form onSubmit={formCreateInvoice.handleSubmit(onFormSubmit)}>
              <div className="flex flex-col space-y-10">
                {/* CAMPAIGN */}
                <div className="flex flex-col gap-5">
                  <h2 className="text-xl font-medium">Campanha</h2>
                  <FormCard>
                    <FormField
                      control={formCreateInvoice.control}
                      name="campaign.name"
                      rules={{ required: 'Campanha é um campo obrigatório' }}
                      render={({ field }) => (
                        <FormItem className="col-span-1 lg:col-span-2">
                          <FormLabel required={true}>
                            Nome da Campanha
                          </FormLabel>
                          <FormControl>
                            <Input
                              disabled={true}
                              value={queryCampaign.data?.campaign?.name}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </FormCard>
                </div>

                {/* CUSTOMER */}
                <div className="flex flex-col gap-5">
                  <h2 className="text-xl font-medium">Cliente</h2>
                  <FormCard>
                    <FormField
                      control={formCreateInvoice.control}
                      name="customer.cc"
                      rules={{
                        required: 'Por favor insira o CC do Cliente',
                      }}
                      render={({ field }) => (
                        <FormItem className="col-span-1 lg:col-span-2">
                          <FormLabel required={true}>
                            Nº Identificação
                          </FormLabel>
                          <FormControl>
                            <DebouncedInput {...field} />
                          </FormControl>

                          {watchCustomerFieldCc === undefined ||
                          watchCustomerFieldCc === '' ? (
                            <FormDescription>
                              Insira o Nº de Identificação para pesquisar o
                              cliente na Base de Dados
                            </FormDescription>
                          ) : queryCustomerFromCc.isLoading ||
                            queryCustomerFromCc.isRefetching ? (
                            <FormDescription className="flex flex-row items-center">
                              <Loader2Icon className="animate-spin dark:text-white" />
                              Por favor aguarde...
                            </FormDescription>
                          ) : queryCustomerFromCc.isError ||
                            queryCustomerFromCc.isRefetchError ? (
                            <FormDescription className="flex flex-row items-center">
                              <AlertCircleIcon className="mr-1.5 size-5" />
                              Não foi possível obter os dados do cliente com o
                              Nº de Identificação {watchCustomerFieldCc}...
                            </FormDescription>
                          ) : !queryCustomerFromCc?.data &&
                            (watchCustomerFieldCc !== '' ||
                              watchCustomerFieldCc !== undefined) ? (
                            <FormDescription className="flex flex-row items-center">
                              <AlertCircleIcon className="mr-2 size-5" />
                              <span>
                                Não existe nenhum cliente com o Nº de
                                Identificação {watchCustomerFieldCc}
                                ...{' '}
                                <button
                                  onClick={() => createClient()}
                                  type="button"
                                  className="underline"
                                >
                                  Carregue aqui para criar o Cliente
                                </button>
                              </span>
                            </FormDescription>
                          ) : null}

                          {formCreateInvoice.formState.errors?.customer ? (
                            <p className="text-sm font-medium text-destructive">
                              Por favor insira um Cliente com Nº de
                              Identificação Válido
                            </p>
                          ) : null}
                        </FormItem>
                      )}
                    />

                    {!queryWatchListCheck.isLoading &&
                    queryWatchListCheck.data?.isInWatchlist ? (
                      <div className="col-span-1 lg:col-span-2">
                        <div className="rounded-md border border-red-500 bg-red-50 p-4 dark:bg-red-950/20">
                          <div className="flex items-start gap-3">
                            <AlertCircleIcon className="mt-0.5 size-5 text-red-600 dark:text-red-400" />
                            <div className="flex-1">
                              <h3 className="font-semibold text-red-800 dark:text-red-400">
                                Atenção: Possível cliente em lista de
                                Funcionários Internos / Lojas!
                              </h3>
                              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                                Este participante pode ser um utilizador
                                inválido. Pode ver as informações do mesmo{' '}
                                <Link
                                  className="underline"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  to={`/backoffice/admin/eligibility-watchlist/${queryWatchListCheck.data.data.ROWID}`}
                                >
                                  aqui
                                </Link>
                                .
                              </p>
                              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                                Antes de avançar verifique os dados do
                                utilizador em questão.
                              </p>
                              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                                Qualquer dúvida verifique com o supervisor antes
                                de avançar.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {!queryCustomerFromCc?.isLoading &&
                    !queryCustomerFromCc?.isRefetching &&
                    queryCustomerFromCc?.data ? (
                      <>
                        <FormField
                          control={formCreateInvoice.control}
                          name="customer.firstName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Nome</FormLabel>
                              <FormControl>
                                <Input disabled={true} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={formCreateInvoice.control}
                          name="customer.lastName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Apelido</FormLabel>
                              <FormControl>
                                <Input disabled={true} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={formCreateInvoice.control}
                          name="customer.birthDate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Data de Nascimento</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  disabled={true}
                                  value={format(
                                    new Date(field.value),
                                    'dd-MM-yyyy',
                                  )}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={formCreateInvoice.control}
                          name="customer.email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email</FormLabel>
                              <FormControl>
                                <Input disabled={true} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={formCreateInvoice.control}
                          name="customer.phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Telemóvel</FormLabel>
                              <FormControl>
                                <Input disabled={true} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={formCreateInvoice.control}
                          name="customer.address"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Morada</FormLabel>
                              <FormControl>
                                <Input disabled={true} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={formCreateInvoice.control}
                          name="customer.district"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Distrito</FormLabel>
                              <Select
                                disabled={true}
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {DISTRICTS.map((item, i) => {
                                    return (
                                      <SelectItem key={i} value={item}>
                                        {item}
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={formCreateInvoice.control}
                          name="customer.zipCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Código Postal</FormLabel>
                              <FormControl>
                                <Input disabled={true} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={formCreateInvoice.control}
                          name="customer.consentTermsOfParticipation"
                          rules={{
                            validate: (value) =>
                              value === true ||
                              'O Cliente necessita de aceitar os termos para participar',
                          }}
                          render={({ field }) => (
                            <FormItem className="col-span-1 lg:col-span-2">
                              <FormLabel required={true}>
                                Condições de Participação
                              </FormLabel>
                              <div className="flex min-h-10 flex-row items-center space-x-2.5">
                                <FormControl>
                                  <Checkbox
                                    disabled={true}
                                    className="size-4"
                                    id="consentTermsOfParticipation"
                                    checked={field.value}
                                  />
                                </FormControl>
                                <span className="text-xs text-portal-text">
                                  Consentimento de Termos de Participação
                                </span>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {!watchCustomerFieldTermsConsent ? (
                          <div className="col-span-1 lg:col-span-2">
                            <div className="rounded-md border border-red-500 bg-red-50 p-4 dark:bg-red-950/20">
                              <div className="flex items-start gap-3">
                                <AlertCircleIcon className="mt-0.5 size-5 text-red-600 dark:text-red-400" />
                                <div className="flex-1">
                                  <h3 className="font-semibold text-red-800 dark:text-red-400">
                                    Consentimento Necessário
                                  </h3>
                                  <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                                    O cliente não aceitou os Termos de
                                    Participação. É necessário atualizar o
                                    consentimento antes de prosseguir.
                                  </p>
                                  <div className="flex flex-row flex-wrap gap-2">
                                    {mutationCreateCustomerConsentRequest.isIdle ||
                                    mutationCreateCustomerConsentRequest.isPending ? (
                                      <Button
                                        type="button"
                                        variant="outline"
                                        className="mt-3 flex flex-row gap-x-2 border-red-600 text-red-600 hover:bg-red-100 dark:border-red-400 dark:text-red-400 dark:hover:bg-red-950/40"
                                        disabled={
                                          mutationCreateCustomerConsentRequest.isPending
                                        }
                                        isLoading={
                                          mutationCreateCustomerConsentRequest.isPending
                                        }
                                        loadingMessage="Por favor aguarde"
                                        onClick={() =>
                                          onSubmitUpdateConsentTerms(
                                            formCreateInvoice.getValues(
                                              'customer',
                                            ),
                                          )
                                        }
                                      >
                                        <PlusCircleIcon />
                                        Criar Pedido de Atualização de
                                        Consentimento do Cliente
                                      </Button>
                                    ) : null}

                                    {mutationCreateCustomerConsentRequest.isSuccess ? (
                                      <Button
                                        type="button"
                                        variant="outline"
                                        className="mt-3"
                                        onClick={() =>
                                          queryCustomerFromCc.refetch()
                                        }
                                      >
                                        <RefreshCwIcon />
                                        Verificar Consentimento do Cliente
                                      </Button>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </>
                    ) : null}
                  </FormCard>
                </div>

                {/* INVOICES */}
                <div className="flex flex-col gap-5">
                  <div className="flex flex-row items-center justify-between">
                    <h2 className="text-xl font-medium">Fatura(s)</h2>
                  </div>

                  {invoicesField.map((item, index) => (
                    <FormCard key={index}>
                      <FormField
                        control={formCreateInvoice.control}
                        name={`invoices.${index}.code`}
                        rules={{
                          required: 'N.º da fatura é um campo obrigatório',
                        }}
                        render={({ field }) => (
                          <FormItem className="col-span-1 mb-3 lg:col-span-2">
                            <FormLabel required={true}>N.º da Fatura</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                disabled={
                                  formCreateInvoice.getValues('campaign') ===
                                  undefined
                                }
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={formCreateInvoice.control}
                        name={`invoices.${index}.amount`}
                        rules={{
                          required: 'Montante da Fatura é um campo obrigatório',
                          validate: (value) => {
                            console.log('hello world?');
                            if (isNaN(Number(value)))
                              return 'Montante da Fatura tem de ser um número';

                            const min = formCreateInvoice.getValues(
                              'campaign.minEntryAmount',
                            );

                            if (Number(value) < min)
                              return 'Montante da Fatura é inferior ao valor de minímo para esta campanha';

                            return true;
                          },
                        }}
                        render={({ field }) => {
                          const couponsToBeGenerated = Math.floor(
                            +field.value / +watchCampaignField?.minEntryAmount,
                          );

                          return (
                            <FormItem>
                              <FormLabel required={true}>
                                Valor da Fatura (€)
                              </FormLabel>
                              <FormControl>
                                <InputCurrency
                                  {...field}
                                  disabled={
                                    formCreateInvoice.getValues('campaign') ===
                                    undefined
                                  }
                                />
                              </FormControl>

                              {field.value !== '' ? (
                                <FormDescription className="flex flex-row items-center">
                                  <InfoIcon className="mr-2 size-4" />
                                  <span>
                                    Equivalente a {couponsToBeGenerated} Cupões
                                  </span>
                                </FormDescription>
                              ) : null}

                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />

                      <FormField
                        control={formCreateInvoice.control}
                        name={`invoices.${index}.issuedDate`}
                        rules={{
                          required:
                            'Data de Emissão da Fatura é um campo obrigatório',
                          pattern: {
                            value: REGEX_DATE_EU,
                            message:
                              'Data de Emissão tem de seguir o formato: dd-MM-yyyy',
                          },
                          validate: {
                            validDate: (value) => {
                              try {
                                const parsedDate = parse(
                                  value,
                                  'dd-MM-yyyy',
                                  new Date(),
                                );
                                return (
                                  isValid(parsedDate) ||
                                  'Insira uma data válida'
                                );
                              } catch (error) {
                                return 'Insira uma data válida';
                              }
                            },
                            dateWithinRange: (value) => {
                              const campaign =
                                formCreateInvoice.getValues('campaign');
                              const parsedIssuedDate = parse(
                                value,
                                DATE_FORMAT,
                                new Date(),
                              );
                              const parsedStartDate = startOfDay(
                                parse(
                                  campaign.startDate,
                                  DATETIME_FORMAT_STANDARD,
                                  new Date(),
                                ),
                              );
                              const parsedCloseDate = startOfDay(
                                parse(
                                  campaign.closeDate,
                                  DATETIME_FORMAT_STANDARD,
                                  new Date(),
                                ),
                              );

                              if (
                                isValid(parsedIssuedDate) &&
                                isValid(parsedStartDate) &&
                                isValid(parsedCloseDate)
                              ) {
                                const isWithin =
                                  (isAfter(parsedIssuedDate, parsedStartDate) ||
                                    isEqual(
                                      parsedIssuedDate,
                                      parsedStartDate,
                                    )) &&
                                  (isBefore(
                                    parsedIssuedDate,
                                    parsedCloseDate,
                                  ) ||
                                    isEqual(parsedIssuedDate, parsedCloseDate));
                                return (
                                  isWithin ||
                                  'Data de Emissão deve estar entre a data de início e a data de término da campanha'
                                );
                              }
                              return false;
                            },
                          },
                        }}
                        render={({ field }) => {
                          let selectedDate = null;

                          if (field?.value?.match(REGEX_DATE_EU)) {
                            const [day, month, year] = field.value.split('-');
                            selectedDate = new Date(`${year}-${month}-${day}`);
                          }

                          return (
                            <FormItem>
                              <FormLabel required={true}>
                                Data de Emissão da Fatura
                              </FormLabel>
                              <FormControl>
                                <DateInputWithDatePicker
                                  input={{
                                    ...field,
                                    onChange: (e) => {
                                      const date = e.target.value;

                                      if (!date && !isValid(new Date(date)))
                                        return;

                                      field.onChange(date);
                                    },
                                  }}
                                  calendar={{
                                    selected: selectedDate,
                                    onSelect: (date) => {
                                      if (!date && !isValid(new Date(date)))
                                        return;

                                      field.onChange(
                                        format(date, 'dd-MM-yyyy'),
                                      );
                                    },
                                  }}
                                />
                              </FormControl>

                              <FormDescription>
                                Data de Emissão tem de seguir o formato:{' '}
                                <i>dd-MM-yyyy</i>
                              </FormDescription>

                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />

                      <FormField
                        control={formCreateInvoice.control}
                        name={`invoices.${index}.shopId`}
                        rules={{ required: 'Loja é um campo obrigatório' }}
                        render={({ field }) => {
                          return (
                            <FormItem>
                              <FormLabel required={true}>Loja</FormLabel>
                              <FormControl>
                                <Combobox
                                  disabled={
                                    !formCreateInvoice.getValues('campaign')
                                  }
                                  items={queryCampaign.data.shops.map(
                                    (item) => ({
                                      label: item.name,
                                      value: item.ROWID,
                                    }),
                                  )}
                                  value={field.value?.label}
                                  valueKey="value"
                                  labelKey="label"
                                  onChange={(value) => field.onChange(value)}
                                  isError={queryCampaign.isError}
                                  isLoading={
                                    queryCampaign.isLoading ||
                                    queryCampaign.isPending ||
                                    queryCampaign.isPaused
                                  }
                                />
                              </FormControl>

                              <FormDescription className="flex flex-row items-center">
                                <InfoIcon className="mr-2 size-4" />
                                <span>A loja onde a fatura foi emitida</span>
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />

                      {index > 0 ? (
                        <div className="col-span-1 mt-2 flex justify-center lg:col-span-2">
                          <Button
                            variant="ghost"
                            type="button"
                            className="text-destructive"
                            onClick={() => removeInvoices(index)}
                          >
                            <MinusCircleIcon /> Remover Fatura
                          </Button>
                        </div>
                      ) : null}
                    </FormCard>
                  ))}

                  <Button
                    variant="outline"
                    type="button"
                    className=""
                    onClick={() =>
                      appendInvoices({
                        code: '',
                        amount: 0,
                        issuedDate: '',
                        shopId: '',
                        campaign: '',
                      })
                    }
                  >
                    <PlusCircleIcon /> Adicionar outra Fatura
                  </Button>
                </div>
              </div>

              <FormFooter>
                <CancelButton />

                <Button
                  type="button"
                  onClick={async () => {
                    const isValid = await formCreateInvoice.trigger();

                    if (isValid) {
                      setSubmitDialog(!submitDialog);
                    }
                  }}
                  disabled={
                    formCreateInvoice.formState.isSubmitting ||
                    !formCreateInvoice.formState.isDirty
                  }
                >
                  {formCreateInvoice.formState.isSubmitting ? (
                    <Loader2Icon className="animate-spin" />
                  ) : (
                    <PlusCircleIcon />
                  )}
                  {formCreateInvoice.formState.isSubmitting
                    ? 'A inserir...'
                    : 'Inserir'}
                </Button>

                <AlertDialog open={submitDialog} onOpenChange={setSubmitDialog}>
                  <AlertDialogContent className="lg:min-w-[820px]">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="pb-2.5 text-xl">
                        Tem a certeza?
                      </AlertDialogTitle>

                      {watchInvoicesField.find((item) => item.amount > 100) ? (
                        <p className="flex flex-row items-center pb-2 text-lg font-bold text-red-500">
                          <AlertCircleIcon className="mr-2 size-6" />
                          {watchInvoicesField.length > 1 ? (
                            <span>
                              O valor de uma das faturas ultrapassa os 100€
                            </span>
                          ) : (
                            <span>O valor da fatura ultrapassa os 100€</span>
                          )}
                        </p>
                      ) : null}

                      <div>
                        <p className="pb-2">
                          Para o cliente,{' '}
                          <b>
                            {watchCustomerFirstNameField}{' '}
                            {watchCustomerLastNameField}
                          </b>
                          , na campanha <b>{watchCampaignField?.name}</b>, serão
                          inseridas a(s) seguinte(s) fatura(s):
                        </p>
                        <div className="flex flex-col gap-y-2">
                          {watchInvoicesField.map((item, index) => {
                            const couponsToBeGenerated = Math.floor(
                              +item.amount /
                                +watchCampaignField?.minEntryAmount,
                            );

                            return (
                              <div key={index}>
                                <p>
                                  {item.code} - {item.amount}€
                                </p>
                                <p className="text-xs">
                                  Equivalente a {couponsToBeGenerated} Cupões
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <AlertDialogDescription className="pt-4 text-lg font-bold text-red-500">
                        Esta ação é irreversível.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>
                        <XIcon />
                        Cancelar
                      </AlertDialogCancel>
                      <AlertDialogAction asChild>
                        <Button
                          variant="destructive"
                          onClick={async () => {
                            const isValid = await formCreateInvoice.trigger();

                            if (isValid) {
                              formCreateInvoice.handleSubmit(onFormSubmit)();
                            }
                          }}
                        >
                          <CheckIcon />
                          Continuar
                        </Button>
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </FormFooter>
            </form>
          </Form>
        </FormUnsavedConfirmationWrapper>
      )}
    </BackOfficePermissionsRedirectWrapper>
  );
}
